import axios from 'axios';
import { parse_response_data } from '@/utils/api_response_helpers';
import { remove_sla_workflow_binding } from '@/services/workflow_service';
import {
  CREATE_SLA_CONFIG_URL,
  CREATE_SLA_WORKFLOW_BINDING_URL,
  UPDATE_SLA_CONFIG_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

export const DEFAULT_CREATE_FORM = () => ({
  id: null,
  name: '',
  description: '',
  target_duration: '',
  warning_threshold: '',
  escalation_duration: '',
  workflow_id: '',
  workflow_locked: false,
  is_active: true,
});

export function createDefaultWorkflowForm() {
  const workflow_form = {
    sla_config_id: null,
    workflow_id: '',
    current_workflow_id: null,
    current_workflow_name: '',
  };
  return workflow_form;
}

export function normalize_positive_id(value) {
  const id = Number(value || 0);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function get_first_defined_value(values) {
  return values.find((value) => value !== undefined && value !== null) ?? null;
}

export function normalize_workflow_option_value(item = {}, index = 0) {
  const id = normalize_positive_id(get_first_defined_value([
    item?.id,
    item?.workflow_id,
    item?.entity_type_workflow_id,
  ]));
  const name = get_first_defined_value([
    item?.name,
    item?.workflow_name,
    item?.title,
  ]);

  return {
    id,
    name: String(name || `Workflow ${index + 1}`).trim(),
  };
}

export function create_workflow_form_from_sla(sla = {}) {
  const workflow_id = get_first_defined_value([sla.workflow_id]);
  const current_workflow_name = get_first_defined_value([sla.workflow_name, sla.workflow_label]) || '';

  return {
    sla_config_id: normalize_positive_id(sla.id),
    workflow_id: workflow_id ? String(workflow_id) : '',
    current_workflow_id: workflow_id,
    current_workflow_name,
  };
}

export function get_first_workflow_assignment(assigned_workflows) {
  return Array.isArray(assigned_workflows) && assigned_workflows.length
    ? assigned_workflows[0]
    : null;
}

export function build_sla_workflow_fields(item = {}, assigned_workflow = null) {
  const workflow_id = get_first_defined_value([
    assigned_workflow?.workflow_id,
    item?.workflow_id,
    item?.entity_type_workflow_id,
    item?.workflow,
  ]);

  return {
    workflow_id,
    workflow_name: get_first_defined_value([
      assigned_workflow?.workflow_name,
      item?.workflow_name,
      item?.entity_type_workflow_name,
    ]) || '',
  };
}

export function get_response_error_message(error, fallback_message) {
  const response_data = parse_response_data(error?.response?.data || {});
  return response_data?.message || error?.message || fallback_message;
}

export function assert_success_response(response_data, fallback_message) {
  if (response_data?.status && response_data.status !== 'success') {
    throw new Error(response_data?.message || fallback_message);
  }
}

export async function run_request_with_loader(vm, {
  lock_key,
  loader_message,
  request,
  on_success,
  failure_message,
}) {
  if (Reflect.get(vm, lock_key)) {
    return false;
  }

  Reflect.set(vm, lock_key, true);
  const loader = await vm.show_loader(loader_message);

  try {
    const response = await request();
    const response_data = parse_response_data(response);
    assert_success_response(response_data, failure_message);
    await on_success?.(response_data);
    return true;
  } catch (error) {
    vm.show_error(get_response_error_message(error, failure_message));
    return false;
  } finally {
    await vm.close_loader(loader);
    Reflect.set(vm, lock_key, false);
  }
}

export async function submit_workflow_assignment(context) {
  if (context.current_workflow_id) {
    const remove_response = await remove_sla_workflow_binding({
      sla_config_id: context.sla_config_id,
      workflow_id: context.current_workflow_id,
    });
    assert_success_response(parse_response_data(remove_response), 'Failed to replace workflow assignment.');
  }

  return axios.post(
    CREATE_SLA_WORKFLOW_BINDING_URL,
    {
      sla_config_id: context.sla_config_id,
      workflow_id: context.workflow_id,
    },
    get_request_config(),
  );
}

export function get_workflow_assignment_context(
  sla_config_id_value,
  workflow_id_value,
  current_workflow_id_value,
) {
  const sla_config_id = normalize_positive_id(sla_config_id_value);

  if (!sla_config_id) {
    return { error: 'Missing SLA context.' };
  }

  const workflow_id = normalize_positive_id(workflow_id_value);
  if (!workflow_id) {
    return { error: 'Select a workflow or choose Unassign.' };
  }

  return {
    sla_config_id,
    workflow_id,
    current_workflow_id: normalize_positive_id(current_workflow_id_value),
  };
}

export function get_unassign_workflow_context(sla_config_id_value, workflow_id_value) {
  const context = get_workflow_assignment_context(sla_config_id_value, workflow_id_value, null);
  return context.error === 'Select a workflow or choose Unassign.'
    ? { error: 'This SLA has no workflow assigned.' }
    : context;
}

export function build_sla_save_payload(vm, is_update) {
  const payload = vm.build_create_payload();

  if (is_update) {
    payload.sla_config_id = Number(vm.create_form.id);
  }

  return payload;
}

export async function submit_sla_config(vm, is_update) {
  return axios.post(
    is_update ? UPDATE_SLA_CONFIG_URL : CREATE_SLA_CONFIG_URL,
    build_sla_save_payload(vm, is_update),
    get_request_config(),
  );
}
